import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router';
import type { ReactNode } from 'react';
import { buttonClassName, Card } from '../../ui';
import {
  getSession,
  listCatalogueByIds,
  listEntries,
  listLibraryItemsByIds,
} from '../../shared/queries';
import type { ExerciseEntryRow } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { formatExerciseDetail } from '../../shared/exerciseLine';
import { formatTime } from '../../shared/formatDate';
import { useInstance } from '../instance/InstanceProvider';
import { resolveLanguage } from '../../shared/language';
import { text } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientLoading, ClientNotFound, ClientPage } from './ClientPage';
import { Check } from './icons';
import { CoachVideo } from './CoachVideo';
import { weekParam, weekQuery } from './origin';
import { setMarked, useSessionProgress } from './progress';
import { ExerciseMedia, useExerciseMedia } from '../media/ExerciseMedia';
import type { ExerciseMediaSet } from '../media/ExerciseMedia';

/**
 * One session, read at arm's length, in a bright room, on a phone propped
 * against a water bottle.
 *
 * That sentence is the whole specification of this screen and it decides most of
 * what is here: the exercise's name and what the coach prescribed are the two
 * largest things on it, at `ink` on a white card — 18.84:1, four times the floor
 * — and everything optional sits underneath in a quieter weight that still
 * clears it. Nothing is abbreviated into a form that needs a legend: the line is
 * built by `formatExerciseDetail`, the same formatter the coach's editor uses,
 * so "3 × 8-10 · 16 kg · rest 90 s" is what both of them read.
 *
 * **Working through it.** Each exercise has a control that marks it done, and
 * the marks survive leaving the screen and coming back — because a client puts
 * the phone down between sets. Marking is optional: a session can be finished
 * with nothing marked and nothing asks about the exercises that were not.
 * Everything about that lives on the device; see `./progress.ts` for what that
 * does and does not mean.
 *
 * **`getSession` takes an id and no tenant filter.** A session belonging to
 * another client of the same coach, a session in another instance, a session in
 * an unpublished week and a session id that never existed all come back as
 * `null` — one answer, from the policies, with nothing to tell them apart. That
 * is why this page renders exactly the same "not found" for all four.
 */
export function SessionDetailPage() {
  return (
    <ClientGuard>
      <SessionDetail />
    </ClientGuard>
  );
}

function SessionDetail() {
  const instance = useInstance();
  const params = useParams();
  const [search] = useSearchParams();
  const sessionId = params['sessionId'] ?? '';
  const progress = useSessionProgress(sessionId);

  /*
   * Where this was opened from. A session reached from Today goes back to Today;
   * one reached from a week goes back to *that* week, including a week the
   * client had scrolled back to on purpose. See `./origin.ts`.
   */
  const fromWeek = weekParam(search);

  const session = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(supabase, sessionId),
  });

  const entries = useQuery({
    queryKey: ['entries', sessionId],
    queryFn: () => listEntries(supabase, sessionId),
    enabled: session.data != null,
  });

  /*
   * What her coach wrote about each exercise, and what the catalogue says about
   * it. Two requests for the whole page rather than two per exercise, and both
   * are additions: an entry renders completely without either of them, which is
   * what makes the catalogue removable (design C1).
   */
  const itemIds = [
    ...new Set(
      (entries.data ?? [])
        .map((entry) => entry.library_item_id)
        .filter((id): id is string => id !== null),
    ),
  ];

  const items = useQuery({
    queryKey: ['library-items', itemIds.join(',')],
    queryFn: () => listLibraryItemsByIds(supabase, itemIds),
    enabled: itemIds.length > 0,
  });

  const catalogIds = [
    ...new Set(
      (items.data ?? []).map((item) => item.catalog_id).filter((id): id is string => id !== null),
    ),
  ];

  const catalogue = useQuery({
    queryKey: ['catalogue-entries', catalogIds.join(',')],
    queryFn: () => listCatalogueByIds(supabase, catalogIds),
    enabled: catalogIds.length > 0,
  });

  const byItemId = new Map((items.data ?? []).map((item) => [item.id, item]));
  const byCatalogId = new Map((catalogue.data ?? []).map((row) => [row.id, row]));
  const media = useExerciseMedia((catalogue.data ?? []).map((row) => row.media_key));

  if (session.isPending) return <ClientLoading />;
  if (!session.data) return <ClientNotFound />;

  const list = entries.data ?? [];
  const marked = new Set(progress.marked);
  const done = list.filter((entry) => marked.has(entry.id)).length;

  return (
    <ClientPage>
      <AppBar
        backTo={
          fromWeek === null
            ? `/c/${instance.slug}/today`
            : `/c/${instance.slug}/week/${String(fromWeek)}`
        }
        backLabel={fromWeek === null ? text('client.session.back') : text('client.week.backToWeek')}
      />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">{session.data.title}</h1>

      {/*
        How far through, as a count and only as a count. No percentage, no bar
        that fills, no grade, and no comparison with any other session
        (`client-tone`, "Progress is shown without being scored").
      */}
      {list.length === 0 ? null : (
        <p className="text-ink-muted mt-1 text-sm">
          {text('client.today.progress', { done, total: list.length })}
        </p>
      )}

      {session.data.coach_note === null ? null : (
        <Card tone="soft" className="mt-4">
          <p className="text-ink text-sm leading-relaxed">{session.data.coach_note}</p>
        </Card>
      )}

      {entries.isPending ? (
        <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p>
      ) : null}

      {entries.data?.length === 0 ? (
        <Card className="mt-4">
          <p className="text-ink-soft text-sm">{text('client.session.noExercises')}</p>
        </Card>
      ) : null}

      <ol className="mt-4 flex flex-col gap-3">
        {list.map((entry) => {
          const item =
            entry.library_item_id === null ? undefined : byItemId.get(entry.library_item_id);
          const stock = item?.catalog_id == null ? undefined : byCatalogId.get(item.catalog_id);

          return (
            <li key={entry.id}>
              <SessionExercise
                entry={entry}
                marked={marked.has(entry.id)}
                onToggle={(next) => {
                  setMarked(sessionId, entry.id, next);
                }}
                cue={item?.cue ?? null}
                description={describe(stock)}
                media={media}
                mediaKey={stock?.media_key ?? null}
                hasVideo={item?.video_path != null}
                coach={instance.coachDisplayName}
              />
            </li>
          );
        })}
      </ol>

      {/*
        The way out of the session, and there is only one. A session is finished
        by saying how it went — the check-in is not skippable — so the control
        that ends it goes to the check-in rather than marking anything itself.
      */}
      {progress.completedAt === null ? (
        <Link
          to={`/c/${instance.slug}/sessions/${sessionId}/check-in${weekQuery(fromWeek)}`}
          className={buttonClassName({ variant: 'accent', size: 'lg', className: 'mt-6' })}
        >
          {text('client.session.finish')}
        </Link>
      ) : (
        <p className="text-ink-soft mt-6 text-sm">
          {text('client.session.completedAt', { time: formatTime(progress.completedAt) })}
        </p>
      )}
    </ClientPage>
  );
}

/**
 * What the catalogue says about a movement, in the words the client reads.
 *
 * **In the product's language, not in English.** The row carries ten languages
 * and keeps them, which is the point of seeding the text rather than the
 * pictures, and `resolveLanguage()` is the same decision the words on every
 * other screen are rendered from — so a client never meets an English paragraph
 * inside a Castilian screen (`client-language`, "No screen is half-translated").
 *
 * A catalogue row that has been deleted, and a row with no text in this
 * language, both produce `null`, and the card simply has no disclosure — not an
 * empty one, and never a paragraph in a language the reader did not ask for.
 */
function describe(stock: { name: string; instructions: unknown } | undefined): string[] | null {
  if (stock === undefined) return null;

  const byLanguage = stock.instructions as Record<string, unknown> | null;
  const steps = byLanguage?.[resolveLanguage()];
  if (!Array.isArray(steps)) return null;

  const text = steps.filter(
    (step): step is string => typeof step === 'string' && step.trim() !== '',
  );
  return text.length === 0 ? null : text;
}

/**
 * One exercise, with the control that marks it done.
 *
 * The marking lives **here** rather than inside `ExerciseCard`, and that is
 * deliberate: `ExerciseCard` is the exercise as the client reads it, and it is
 * rendered by three tests that assert its text exactly, character for character,
 * against the coach's own line. A toggle added inside it would have changed what
 * those tests measure while appearing to leave them passing.
 *
 * The control is a `button` with `aria-pressed`, not a checkbox: nothing is
 * being submitted, there is no form, and a checkbox would promise one. Its label
 * names the exercise, so a client who reaches it by voice or by a screen reader
 * knows which of six they are about to mark.
 */
export function SessionExercise({
  entry,
  marked,
  onToggle,
  cue,
  description,
  media,
  mediaKey,
  hasVideo = false,
  coach,
}: {
  entry: ExerciseEntryRow;
  marked: boolean;
  onToggle: (next: boolean) => void;
  cue?: string | null | undefined;
  description?: string[] | null | undefined;
  media?: ExerciseMediaSet | undefined;
  mediaKey?: string | null | undefined;
  /** Whether her coach has recorded this movement. */
  hasVideo?: boolean;
  coach: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        aria-pressed={marked}
        aria-label={
          marked
            ? text('client.session.unmarkLabel', { name: entry.name })
            : text('client.session.markLabel', { name: entry.name })
        }
        onClick={() => {
          onToggle(!marked);
        }}
        className={[
          'mt-1 flex size-11 shrink-0 items-center justify-center rounded-full border transition-colors',
          marked
            ? 'bg-accent text-on-accent border-primary'
            : 'bg-surface-raised border-border-strong',
        ].join(' ')}
      >
        {/*
          The tick appears when the exercise is done and not before. A faint one
          sitting in every unmarked circle reads, at a glance and at arm's
          length, as a session already finished — which is the opposite of what
          it means. Unmarked is an empty ring, which is a shape people already
          know how to press.
        */}
        {marked ? <Check size={22} /> : null}
      </button>

      <div className="min-w-0 flex-1">
        <ExerciseCard
          entry={entry}
          cue={cue}
          description={description}
          media={media}
          mediaKey={mediaKey}
          video={hasVideo ? <CoachVideo coach={coach} /> : null}
        />
      </div>
    </div>
  );
}

/**
 * The client's summary of an entry. Built by the same formatter the coach's
 * `EntryLine` uses, so the two cannot drift apart again (design.md E5).
 * Exported for the tests that render both and compare them.
 *
 * Everything past the line itself is an **addition** and every one of them is
 * optional: her coach's cue, her recording, a general description of the
 * movement, a demonstration. An entry with none of them renders exactly as it
 * did before any of this existed, which is what "a published plan survives the
 * catalogue being removed" means in a component — and is why every one of these
 * props defaults to nothing.
 */
export function ExerciseCard({
  entry,
  cue = null,
  description = null,
  media,
  mediaKey = null,
  video = null,
}: {
  entry: ExerciseEntryRow;
  /** Her coach's standing cue for this movement, where she wrote one. */
  cue?: string | null | undefined;
  /** The catalogue's description, as steps. Null when there is none. */
  description?: string[] | null | undefined;
  media?: ExerciseMediaSet | undefined;
  mediaKey?: string | null | undefined;
  /** Her coach's recording, where she has made one. Nothing at all where not. */
  video?: ReactNode;
}) {
  const detail = formatExerciseDetail(entry);

  return (
    <Card>
      {/*
        One flowing line rather than a name with the detail stacked under it:
        the scenario describes what the client reads as a single line, and it
        has to be the same line the coach sees. Wrapping does the work on a
        phone.
      */}
      <p>
        <span className="text-ink font-medium">{entry.name}</span>
        {detail === '' ? null : <span className="text-ink-soft text-sm"> · {detail}</span>}
      </p>
      {entry.note === null ? null : <p className="text-ink-muted mt-2 text-sm">{entry.note}</p>}

      {/*
        Her coach's words sit above the catalogue's, always. The cue is her
        instruction for this movement and is presented as plainly as the entry's
        own note, because to the client they are the same kind of thing: things
        her coach told her.
      */}
      {cue === null || cue.trim() === '' ? null : <p className="text-ink mt-2 text-sm">{cue}</p>}

      {/*
        An exercise with no recording renders with nothing here — no grey box, no
        disabled play control, no caption promising something that is not
        coming. Most exercises will never have one.
      */}
      {video}

      {description === null ? null : (
        <HowToDoIt name={entry.name} steps={description} media={media} mediaKey={mediaKey} />
      )}
    </Card>
  );
}

/**
 * "How to do it", collapsed, in the client's own words about whose words these
 * are.
 *
 * Three things about its shape are requirements rather than taste:
 *
 *   * it is **a plain text link**, never a thumbnail and never a play-shaped
 *     card, because a card that looks like a video promises a video;
 *   * it says, where the reader will actually see it, that this is a general
 *     description of the movement and **not** something her coach wrote. A
 *     client must never mistake a stock description for her coach's
 *     instruction — which for a coach who is also a medical student is not a
 *     presentational concern;
 *   * nothing in it offers the text or the demonstration for download, export
 *     or sharing.
 *
 * `<details>` rather than a button and state: it is closed by default, it opens
 * without JavaScript, and a browser's find-in-page can still reach inside it.
 *
 *   exercise-catalogue, "The catalogue's description is shown to the client as
 *   a general description, never as the coach's words"
 *     #### Scenario: The client can tell whose words these are
 */
function HowToDoIt({
  name,
  steps,
  media,
  mediaKey = null,
}: {
  name: string;
  steps: string[];
  media?: ExerciseMediaSet | undefined;
  mediaKey?: string | null;
}) {
  return (
    <details className="mt-2">
      <summary className="text-primary cursor-pointer text-sm underline-offset-2 hover:underline">
        {text('client.session.howTo')}
      </summary>

      <div className="mt-2 flex flex-col gap-2">
        <p className="text-ink-muted text-xs">{text('client.session.howToDisclaimer')}</p>

        {media === undefined ? null : (
          <ExerciseMedia media={media} mediaKey={mediaKey} name={name} className="w-44" />
        )}

        <ol className="text-ink-soft flex list-decimal flex-col gap-1 pl-5 text-sm">
          {steps.map((step, index) => (
            <li key={`${String(index)}-${step.slice(0, 12)}`}>{step}</li>
          ))}
        </ol>
      </div>
    </details>
  );
}
